<!-- src/views/OnBoarding/RolesResponsibilitiesFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <FormHeader />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- Premium Header Card -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight">Roles & Responsibilities</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Operational Accountability • Sentinel Safe Premium
              </p>
            </div>
            <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest">Official Policy</span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">
          
          <!-- Introductory Help Text -->
          <div class="mb-10 px-4">
            <p class="text-slate-500 font-bold uppercase text-[10px] tracking-widest leading-relaxed">
              Please read the statement below carefully. You must confirm your understanding and agreement before submission.
            </p>
          </div>

          <!-- Dynamic Introductory Text -->
          <section 
            v-if="isLoadingText || (mainTextContent && mainTextContent !== '<p><br></p>')" 
            class="mb-12 p-8 bg-slate-50 rounded-3xl border border-slate-200 shadow-inner"
          >
            <div v-if="isLoadingText" class="text-slate-400 animate-pulse flex items-center gap-2">
               <div class="w-4 h-4 bg-slate-200 rounded-full"></div>
               <span class="font-black uppercase text-xs">Loading Instructions...</span>
            </div>
            <div v-else class="ql-editor !p-0 text-slate-700" v-html="mainTextContent"></div>
          </section>

          <form @submit.prevent="submitForm" class="space-y-16">

            <!-- Section 1: Personnel Details -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <IdentificationIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Your Details</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Full Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.name || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Email</label>
                  <p class="text-lg font-black text-slate-800 lowercase tracking-tight">{{ userProfile.email || 'Loading...' }}</p>
                </div>
              </div>
            </section>

            <!-- Section 2: Statement -->
            <section v-if="!isLoadingText && statementText">
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <AcademicCapIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Roles & Responsibilities Statement</h2>
              </div>
              
              <div class="p-8 bg-slate-50 border border-slate-200 rounded-[32px] shadow-inner">
                <div class="ql-editor !p-0 text-slate-700" v-html="statementText"></div>
              </div>
            </section>
            
            <section v-if="isLoadingText" class="text-center py-10 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
              <div class="animate-spin h-8 w-8 border-4 border-[#20FCF1] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p class="text-[10px] font-black uppercase tracking-widest text-slate-400">Loading statement text...</p>
            </section>

            <!-- Section 3: Confirmation -->
            <section class="bg-slate-900 p-10 rounded-[40px] text-white shadow-xl relative overflow-hidden">
                <div class="absolute top-0 right-0 p-10 opacity-5">
                  <ShieldCheckIcon class="h-32 w-32" />
                </div>
                <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div class="max-w-xl">
                      <h2 class="text-2xl font-black text-[#20FCF1] uppercase tracking-tight mb-2">3. Policy Confirmation</h2>
                      <p class="text-slate-400 font-medium">Please confirm you have read and understood the obligations outlined above.</p>
                    </div>
                    <div class="bg-white/5 p-6 rounded-[32px] border border-white/10">
                        <div class="flex items-start">
                            <input id="confirmation" type="checkbox" v-model="formData.confirmation" class="h-10 w-10 text-[#20FCF1] border-white/20 rounded-xl bg-transparent">
                            <label for="confirmation" class="ml-4 block text-xs font-black uppercase tracking-widest text-white leading-tight">I Agree to the<br>Responsibilities <span class="text-red-500">*</span></label>
                        </div>
                    </div>
                </div>
            </section>
            
            <!-- Section 4: Signature -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PencilSquareIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Verification Signature</h2>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                  <div class="max-w-md mx-auto">
                    <label class="form-label text-slate-400 text-center mb-6">Profile Linked Signature</label>
                    <div class="bg-white p-8 rounded-[32px] border-2 border-dashed border-slate-200 shadow-inner flex flex-col items-center">
                        <div v-if="userProfile.signatureUrl || userProfile.signatureDataUrl" class="animate-fadeIn">
                            <img :src="userProfile.signatureUrl || userProfile.signatureDataUrl" alt="Signature Preview" class="max-h-24 object-contain" />
                            <div class="mt-6 flex items-center justify-center gap-2 bg-[#EBFDF8] px-4 py-2 rounded-full border border-[#20FCF1]/20">
                              <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
                              <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Verified Signature</span>
                            </div>
                        </div>
                        <div v-else class="text-center space-y-4">
                            <div class="bg-red-50 p-4 rounded-xl text-red-600 text-xs font-bold uppercase tracking-tight border border-red-100">
                                Profile Signature Missing
                            </div>
                            <button @click="$router.push({ name: 'yourDetails' })" type="button" class="px-6 py-2 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest">
                                Fix Now
                            </button>
                        </div>
                    </div>
                  </div>
              </div>
            </section>

            <div class="pt-10">
              <button type="submit" :disabled="isSubmitting || !isFormValid" class="btn-premium">
                <span v-if="isSubmitting" class="flex items-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Processing Submission...
                </span>
                <span v-else>Submit Roles & Responsibilities</span>
              </button>
            </div>
          </form>

          <div v-if="successMessage" class="mt-8 p-6 bg-mint-50 border border-brand-accent rounded-[32px] text-center shadow-lg animate-bounce">
            <p class="text-brand-primary font-black uppercase tracking-widest">{{ successMessage }}</p>
          </div>
          <div v-if="errorMessage" class="mt-8 p-6 bg-red-50 border border-red-200 rounded-[32px] text-center font-bold text-red-700">
            {{ errorMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRouter } from 'vue-router';
import FormHeader from '@/components/FormHeader.vue';

// Premium Icons
import { 
  IdentificationIcon, 
  AcademicCapIcon, 
  ShieldCheckIcon, 
  PencilSquareIcon, 
  CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

const router = useRouter();
const { user } = useAuth();
const functions = getFunctions(firebaseApp, 'europe-west2');

const userProfile = reactive({ name: '', email: '', signatureUrl: '', signatureDataUrl: '' });
const formData = reactive({ confirmation: false });

const mainTextContent = ref('');
const statementText = ref('');
const isLoadingText = ref(true);
const successMessage = ref('');
const errorMessage = ref('');
const isSubmitting = ref(false);

const isFormValid = computed(() => {
    return formData.confirmation && (userProfile.signatureUrl || userProfile.signatureDataUrl);
});

const loadPrerequisites = async () => {
  if (!user.value) {
    errorMessage.value = "You must be logged in to complete this form.";
    isLoadingText.value = false;
    return;
  }
  
  try {
    isLoadingText.value = true;
    const userProfileRef = doc(db, 'userProfiles', user.value.uid);
    const idTokenResult = await user.value.getIdTokenResult();
    const clientId = idTokenResult.claims.clientId;

    if (!clientId) {
      throw new Error("Client association not found. Please contact support.");
    }

    const clientDocRef = doc(db, 'clients', clientId);

    const [userProfileSnap, clientDocSnap] = await Promise.all([
      getDoc(userProfileRef),
      getDoc(clientDocRef)
    ]);

    // Process user profile
    if (userProfileSnap.exists()) {
      const profile = userProfileSnap.data();
      userProfile.name = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
      userProfile.email = profile.email || user.value.email;
      userProfile.signatureUrl = profile.signatureUrl || '';
      userProfile.signatureDataUrl = profile.signatureDataUrl || '';
    } else {
      throw new Error("Please complete the 'Your Details' form first.");
    }

    // Process client-specific form texts
    if (clientDocSnap.exists()) {
      const clientData = clientDocSnap.data();
      mainTextContent.value = clientData.onboardingFormTexts?.rolesResponsibilitiesIntro || '';
      statementText.value = clientData.onboardingFormTexts?.rolesAndResponsibilities || '';
    }

  } catch (error) {
    errorMessage.value = `Error loading prerequisites: ${error.message}`;
  } finally {
    isLoadingText.value = false;
  }
};

onMounted(loadPrerequisites);

const submitForm = async () => {
  if (!isFormValid.value) {
    errorMessage.value = "Please confirm the statement and ensure your signature is available.";
    return;
  }
  
  isSubmitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  const submissionData = {
    ...formData,
    name: userProfile.name,
    email: userProfile.email,
    signatureUrl: userProfile.signatureUrl,
    signatureDataUrl: userProfile.signatureDataUrl,
    status: 'completed',
    statementText: statementText.value
  };

  try {
    const submitFormFunction = httpsCallable(functions, 'submitGenericForm');
    const result = await submitFormFunction({
      formIdentifier: 'rolesAndResponsibilities',
      targetCollection: 'onboardingFormSubmissions',
      submissionData: submissionData
    });

    if (result.data.success) {
      successMessage.value = `Form submitted successfully! Reference: ${result.data.referenceNumber}`;
      setTimeout(() => router.push('/onboarding-forms'), 2000);
    } else {
      throw new Error(result.data.error || 'The form submission failed.');
    }

  } catch (error) {
    errorMessage.value = `Failed to submit form: ${error.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-input { 
  @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 shadow-sm transition-all focus:ring-2 focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm; 
}
.form-label { 
  @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; 
}
.btn-premium { 
  @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale flex justify-center items-center; 
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Quill Styling */
:deep(.ql-editor) {
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #334155;
}
:deep(.ql-editor p) {
  @apply mb-4;
}
:deep(.ql-editor h2), :deep(.ql-editor h3) {
  @apply text-xl font-black uppercase tracking-tight text-slate-900 mt-8 mb-4;
}
</style>